// ---------------------------------------------------------------------------
// useTemplateRunVariables — resolves the set of variables a hosted run can
// override, mirroring the server's merge policy (see WorkflowSvc
// `MergeRunVariables`): declarations come from the referenced agent(s), the
// referenced grader(s), and the template itself, with the template taking
// precedence over the entity declarations.
//
// The run API accepts these as per-run `variables` overrides (applied on top
// of the resolved snapshot's declared defaults at execution time) without
// touching any stored entity or creating a new version. This hook only reads
// the declarations so the Queue dialog can prefill each override with its
// declared default.
//
// Note: the entity `variables` come from each resource's current working copy
// (the list/get endpoints), while a run resolves the latest *snapshot*. The
// declared variable set rarely differs between the two, and the server applies
// overrides by name regardless — so this is a faithful preview, not a binding
// contract.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';

import { useHttpClient } from '../../../api/HttpClientContext.js';
import { AgentsService } from '../../../services/agentsService.js';
import { WorkflowApiService } from '../../../services/workflowApi.js';
import type { EvaluationTemplateWithConfig } from '../EvaluationTemplateForm/EvaluationTemplateForm.js';

export type RunVariableSource = 'template' | 'agent' | 'grader';

export interface RunVariable {
  name: string;
  /** Declared type — drives the input control ('boolean' | 'number' | ...). */
  type: string;
  /** Declared default, prefilled into the override field. May be undefined. */
  default?: unknown;
  description?: string;
  /** Where the declaration came from — surfaced to help the user. */
  source: RunVariableSource;
}

export interface UseTemplateRunVariablesResult {
  variables: RunVariable[];
  isLoading: boolean;
  error: string | null;
  resolved: boolean;
}

interface ReferencedNames {
  agentNames: string[];
  graderNames: string[];
}

function isOverridableDefault(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return true;
}

function collectReferencedNames(
  template: EvaluationTemplateWithConfig | null,
): ReferencedNames {
  const agentNames = new Set<string>();
  const graderNames = new Set<string>();
  for (const task of template?.tasks ?? []) {
    if (task.agent?.name) agentNames.add(task.agent.name);
    for (const grader of task.graders ?? []) {
      if (grader.name) graderNames.add(grader.name);
    }
  }
  return {
    agentNames: [...agentNames].sort(),
    graderNames: [...graderNames].sort(),
  };
}

/**
 * Resolves the overridable variables for a template's hosted run.
 *
 * @param workspaceName - Workspace the template belongs to.
 * @param template - The loaded template (with its tasks + variables), or null.
 * @param enabled - When false, the hook stays idle (no fetch). Pass the
 *   dialog's open state so resolution runs only while the dialog is shown.
 */
export function useTemplateRunVariables(
  workspaceName: string,
  template: EvaluationTemplateWithConfig | null,
  enabled: boolean,
): UseTemplateRunVariablesResult {
  const httpClient = useHttpClient();
  const agentsService = useMemo(
    () => new AgentsService(httpClient),
    [httpClient],
  );
  const workflowApi = useMemo(
    () => new WorkflowApiService(httpClient),
    [httpClient],
  );

  const { agentNames, graderNames } = useMemo(
    () => collectReferencedNames(template),
    [template],
  );
  // Stable primitive deps so the effect only re-runs when the referenced set
  // (not the template object identity) actually changes.
  const agentKey = agentNames.join('\u0000');
  const graderKey = graderNames.join('\u0000');
  const templateVariablesKey = JSON.stringify(template?.variables ?? {});
  const resolutionKey = `${enabled ? '1' : '0'}\u0001${workspaceName}\u0001${agentKey}\u0001${graderKey}\u0001${templateVariablesKey}`;

  const [variables, setVariables] = useState<RunVariable[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || template === null) {
      return;
    }

    const agentList = agentKey.length > 0 ? agentKey.split('\u0000') : [];
    const graderList = graderKey.length > 0 ? graderKey.split('\u0000') : [];

    const templateVars: RunVariable[] = Object.entries(template.variables ?? {})
      .filter(([, decl]) => isOverridableDefault(decl.default))
      .map(([name, decl]) => ({
        name,
        type: decl.type ?? 'string',
        default: decl.default,
        description: decl.description,
        source: 'template' as const,
      }));

    // No referenced agents/graders → nothing to fetch. Resolve synchronously
    // from the template's own declarations so callers (e.g. the queue button)
    // can decide immediately whether any override UI is needed, with no
    // loading flicker.
    if (agentList.length === 0 && graderList.length === 0) {
      setVariables(
        [...templateVars].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setError(null);
      setIsLoading(false);
      setResolvedKey(resolutionKey);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void (async () => {
      // Resolve each referenced entity independently: a failed agent/grader
      // fetch (e.g. a name/version mismatch) must not wipe the whole set —
      // template-level variables (and any entities that did resolve) stay
      // overridable. We only surface an error if nothing could be resolved.
      const [agentResults, graderResults] = await Promise.all([
        Promise.allSettled(
          agentList.map((name) => agentsService.get(workspaceName, name)),
        ),
        Promise.allSettled(
          graderList.map((name) => workflowApi.getGrader(workspaceName, name)),
        ),
      ]);
      if (cancelled) return;

      // Merge order encodes precedence: entity declarations form the base
      // (first writer wins across agents/graders), then template
      // declarations override by name — mirroring MergeRunVariables.
      const merged = new Map<string, RunVariable>();
      const addEntity = (
        source: RunVariableSource,
        decls?: Record<
          string,
          { type: string; default?: unknown; description?: string }
        >,
      ) => {
        for (const [name, decl] of Object.entries(decls ?? {})) {
          if (!isOverridableDefault(decl.default)) continue;
          if (!merged.has(name)) {
            merged.set(name, {
              name,
              type: decl.type,
              default: decl.default,
              description: decl.description,
              source,
            });
          }
        }
      };
      for (const r of agentResults) {
        if (r.status === 'fulfilled') addEntity('agent', r.value.variables);
      }
      for (const r of graderResults) {
        if (r.status === 'fulfilled') addEntity('grader', r.value.variables);
      }
      for (const tv of templateVars) {
        merged.set(tv.name, tv);
      }

      setVariables(
        [...merged.values()].sort((a, b) => a.name.localeCompare(b.name)),
      );

      // Surface a soft error only when a referenced entity failed to resolve
      // AND we recovered nothing — otherwise the dialog is usable as-is.
      const firstFailure = [...agentResults, ...graderResults].find(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );
      if (firstFailure !== undefined && merged.size === 0) {
        setError(
          firstFailure.reason instanceof Error
            ? firstFailure.reason.message
            : 'Failed to resolve run parameters.',
        );
      }
      setIsLoading(false);
      setResolvedKey(resolutionKey);
    })();

    return () => {
      cancelled = true;
    };
    // agentKey/graderKey/templateVariablesKey are stable string projections of
    // the referenced set and template declarations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    workspaceName,
    agentKey,
    graderKey,
    templateVariablesKey,
    agentsService,
    workflowApi,
  ]);

  const resolved = enabled && resolvedKey === resolutionKey;

  return useMemo(
    () => ({ variables, isLoading, error, resolved }),
    [variables, isLoading, error, resolved],
  );
}
